import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Check, Circle, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { TenXBrandFooter } from "@/components/TenXBrandFooter";

export default function PublicDiligence() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/dd/${token}`, { quiet402: true })
      .then(({ data }) => setData(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div data-testid="dd-public-loading" className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-6 text-center" data-testid="dd-public-error">
        <ShieldCheck className="h-10 w-10 text-slate-300" />
        <h1 className="ff-cabinet text-xl font-bold text-slate-900">Diligence link unavailable</h1>
        <p className="max-w-sm text-sm text-slate-500">This diligence index is invalid or has been revoked by the company.</p>
        <TenXBrandFooter context="diligence" className="mt-6" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-5 py-10" data-testid="dd-public-page">
      <div className="mx-auto w-full max-w-2xl">
        <div className="flex items-center justify-between">
          <Logo />
          <span className="ff-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">Diligence index</span>
        </div>

        <Card className="mt-6 rounded-2xl border p-7">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="ff-cabinet text-2xl font-bold tracking-tight text-slate-900">{data.company_name}</h1>
              <p className="text-sm text-slate-500">Due-diligence readiness overview</p>
            </div>
            <div className="text-right">
              <div className="h-display text-3xl font-bold text-foreground" data-testid="dd-public-pct">{data.pct}%</div>
              <div className="text-xs text-slate-500">{data.ready}/{data.total} items ready</div>
            </div>
          </div>
          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full rounded-full bg-brand transition-[width]" style={{ width: `${data.pct}%` }} />
          </div>
        </Card>

        <div className="mt-4 space-y-3">
          {data.categories.map((cat) => (
            <Card key={cat.category} className="rounded-xl border p-5" data-testid={`dd-public-cat-${cat.category.replace(/\W+/g, "-").toLowerCase()}`}>
              <div className="mb-2 flex items-center justify-between">
                <h2 className="ff-cabinet text-sm font-bold text-slate-900">{cat.category}</h2>
                <span className="text-xs text-slate-400">{cat.items.filter((i) => i.ready).length}/{cat.items.length}</span>
              </div>
              <div className="divide-y">
                {cat.items.map((it) => (
                  <div key={it.key} className="flex items-center gap-2.5 py-2 text-sm">
                    {it.ready ? <Check className="h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.4} /> : <Circle className="h-4 w-4 shrink-0 text-slate-300" />}
                    <span className={it.ready ? "font-medium text-slate-800" : "text-slate-400"}>{it.label}</span>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
        <TenXBrandFooter context="diligence" className="mt-6" notice="Read-only" />
      </div>
    </div>
  );
}
